import React, { useRef, useState } from 'react';

import { Button } from '@/design-system/button';
import {
  FileUploadDropzone,
  FileUploadItem,
  FileUploadList,
} from '@/design-system/file-upload';

import {
  isAbortError,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';

/** Matches WAREHOUSE_IMAGE_MAX_BYTES on the server. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;

const ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
const isImage = (file: File) =>
  ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
  /\.(jpe?g|png|webp)$/i.test(file.name);

/**
 * One sample image of a SKU, uploaded the moment it is picked (straight to
 * the main bucket) so the form only ever carries a stored URL — the same
 * shape as DocumentUpload, with a thumbnail instead of a "Buka PDF" link.
 * A stored image can be replaced or removed; removing while an upload runs
 * aborts it and ignores its late answer.
 */
export const ImageUpload: React.FC<{
  value: string | null;
  onChange: (url: string | null) => void;
  onBusyChange?: (busy: boolean) => void;
  idPrefix?: string;
  testId?: string;
}> = ({ value, onChange, onBusyChange, idPrefix = 'img', testId }) => {
  const [pending, setPending] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = useRef<{ token: number; abort: AbortController } | null>(
    null
  );
  const tokens = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const onBusyRef = useRef(onBusyChange);
  onBusyRef.current = onBusyChange;

  const retire = () => {
    if (!current.current) return;
    current.current.abort.abort();
    current.current = null;
    onBusyRef.current?.(false);
  };

  const upload = async (file: File) => {
    retire();
    const mine = { token: ++tokens.current, abort: new AbortController() };
    current.current = mine;
    const live = () => current.current?.token === mine.token;
    setPending(file);
    setError(null);
    onBusyRef.current?.(true);
    try {
      const img = await warehouseApi.uploadImage(file, {
        signal: mine.abort.signal,
      });
      if (!live()) return;
      onChange(img.fileUrl);
      setPending(null);
    } catch (err) {
      if (!live() || isAbortError(err)) return;
      setError(parseWarehouseError(err).message || 'Gagal mengunggah');
    } finally {
      if (live()) {
        current.current = null;
        onBusyRef.current?.(false);
      }
    }
  };

  const pick = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (!isImage(file)) {
      retire();
      setPending(file);
      setError('Gambar harus JPG, PNG, atau WebP.');
      return;
    }
    void upload(file);
  };

  const clear = () => {
    retire();
    setPending(null);
    setError(null);
    onChange(null);
  };

  if (value && !pending) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-stroke-soft-200 p-2"
        data-testid={testId}
      >
        <a href={value} target="_blank" rel="noreferrer" className="shrink-0">
          <img
            src={value}
            alt="Foto barang"
            className="size-16 rounded-md border border-stroke-soft-200 object-cover"
          />
        </a>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-[12px] text-text-sub-600">Tersimpan</p>
          <div className="flex gap-2">
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              onClick={() => fileInput.current?.click()}
            >
              Ganti
            </Button>
            <Button
              tone="destructive"
              style="ghost"
              size="xs"
              onClick={clear}
              aria-label="Hapus gambar"
            >
              Hapus
            </Button>
          </div>
        </div>
        <input
          ref={fileInput}
          id={`${idPrefix}-replace`}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          aria-label="Ganti gambar"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            pick(files);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {pending ? (
        <FileUploadList>
          <FileUploadItem
            name={pending.name}
            size={pending.size}
            mime={pending.type || 'image/jpeg'}
            status={error ? 'error' : 'uploading'}
            errorMessage={error ?? undefined}
            onRetry={
              error && isImage(pending) ? () => void upload(pending) : undefined
            }
            onRemove={clear}
          />
        </FileUploadList>
      ) : (
        <FileUploadDropzone
          accept={ACCEPT}
          multiple={false}
          maxBytes={IMAGE_MAX_BYTES}
          onFiles={pick}
          title="Pilih foto barang atau seret ke sini"
          description="JPG, PNG, atau WebP, maksimal 5 MB"
          browseLabel="Pilih gambar"
        />
      )}
    </div>
  );
};
